import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, Inbox, LogOut, Users } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { CaseListItem, SearchHit } from "../../../../shared/contracts.js";
import {
  flattenPages,
  useCaseDetail,
  useCaseList,
  useCaseSearch,
} from "../../../api/caseQueries.js";
import { useSession } from "../../../app/AppProviders.js";
import { LOGIN_PATH } from "../../../app/RequireSession.js";
import { AsyncPanel, signOutFailureMessage } from "../../../ui/AsyncPanel.js";
import { Button, ControlSizeProvider } from "../../../ui/Button.js";
import { StatusBadge } from "../../../ui/StatusBadge.js";
import { SharedCaseDetail } from "../detail/SharedCaseDetail.js";
import {
  defaultSharedView,
  parseSharedView,
  ROLE_LABELS,
  SharedNavigation,
  sharedViewDefinition,
  sharedViewPath,
  type SharedView,
} from "./SharedNavigation.js";

/**
 * The shared Surface Pro workspace (spec §11) for advisors, technicians,
 * service managers, shop foremen and dispatchers: a touch-friendly split view
 * of navigation, a 305 px case list and a fluid detail. Every control is at
 * least 44×44 CSS px (`ControlSizeProvider size="touch"`).
 *
 * URL state: `view` (one of the six destinations; defaults by role), `case`
 * (the open case) and `q` (the Search all query). Selecting a row rewrites
 * `case` in place; changing views drops it. At 1368×912 the navigation rail
 * is persistent; at 1024×768 it collapses into the labelled view picker; below
 * 1024 px the list and detail take turns (`data-has-case`) with a Back link —
 * a graceful fallback, not a phone design.
 *
 * The page renders what the server decided. Membership in a view, status,
 * the 48-hour window, unread state and permissions all arrive in the DTOs.
 */

const SEARCH_DEBOUNCE_MS = 300;

export const LIFECYCLE_LABELS: Readonly<Record<SearchHit["lifecycle"], string>> = {
  active: "Active",
  arrived_48h: "Arrived · 48h",
  historical: "Historical",
  cancelled: "Cancelled",
};

const rowTimeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatRowTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : rowTimeFormatter.format(date);
}

/** A value that follows `value` after it has stopped changing for `delayMs`. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function SharedWorkspacePage() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The route guard guarantees a session; the fallback only satisfies types.
  const role = user?.role ?? "dispatcher";
  const view: SharedView =
    parseSharedView(searchParams.get("view")) ?? defaultSharedView(role);
  const definition = sharedViewDefinition(view);
  const selectedCaseId = searchParams.get("case");
  const rawQuery = view === "search" ? (searchParams.get("q") ?? "") : "";

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => navigate(LOGIN_PATH, { replace: true }),
  });

  const setQuery = (next: string) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set("view", "search");
        if (next) params.set("q", next);
        else params.delete("q");
        return params;
      },
      { replace: true },
    );
  };

  const rowHref = (caseId: string) =>
    sharedViewPath(view, { q: rawQuery, case: caseId });
  const backHref = sharedViewPath(view, { q: rawQuery });

  return (
    <ControlSizeProvider size="touch">
      <div
        className="shared-workspace"
        data-view={view}
        data-has-case={selectedCaseId ? "true" : undefined}
      >
        <header className="shared-chrome">
          <p className="shared-brand">Chicagoland Auto Tools</p>
          <SharedNavigation view={view} searchQuery={rawQuery} />
          <div className="shared-account">
            {user ? (
              <p className="shared-account__who">
                <span className="shared-account__name">{user.displayName}</span>
                <span className="shared-account__role">{ROLE_LABELS[user.role]}</span>
              </p>
            ) : null}
            {user?.isAdmin ? (
              // Administration is a capability on the account, not a role (spec §2): the
              // link follows `isAdmin`, and the server checks it on every /api/accounts call.
              <Link to="/accounts" className="shared-account__admin">
                <Users aria-hidden="true" />
                Accounts
              </Link>
            ) : null}
            {signOut.isError ? (
              <p role="alert" className="form-alert">
                {signOutFailureMessage(signOut.error)}
              </p>
            ) : null}
            <Button
              variant="quiet"
              icon={<LogOut />}
              busy={signOut.isPending}
              onClick={() => signOut.mutate()}
            >
              Sign out
            </Button>
          </div>
        </header>

        <main className="shared-main">
          <section className="shared-list" aria-label="Cases">
            <div className="shared-list__header">
              <h1 className="shared-list__title">{definition.label}</h1>
              {view === "search" ? (
                <SearchField value={rawQuery} onChange={setQuery} />
              ) : null}
            </div>
            {view === "search" ? (
              <SearchResults
                query={rawQuery}
                selectedCaseId={selectedCaseId}
                rowHref={rowHref}
              />
            ) : (
              <CaseList
                view={view}
                selectedCaseId={selectedCaseId}
                rowHref={rowHref}
              />
            )}
          </section>

          <section className="shared-detail-pane" aria-label="Case detail">
            {selectedCaseId ? (
              <DetailPane caseId={selectedCaseId} backHref={backHref} />
            ) : (
              <div className="shared-detail-pane__empty">
                <Inbox aria-hidden="true" focusable="false" />
                <p>Select a case to see its details.</p>
              </div>
            )}
          </section>
        </main>
      </div>
    </ControlSizeProvider>
  );
}

// ---------------------------------------------------------------------------
// List pane
// ---------------------------------------------------------------------------

function SearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const id = useId();
  return (
    <div className="shared-search">
      <label htmlFor={id} className="shared-search__label">
        Search all cases
      </label>
      <input
        id={id}
        type="search"
        className="text-input shared-search__input"
        data-size="touch"
        defaultValue={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="RO, case, VIN, customer, part, advisor…"
        autoComplete="off"
        maxLength={120}
        enterKeyHint="search"
      />
    </div>
  );
}

function CaseList({
  view,
  selectedCaseId,
  rowHref,
}: {
  view: SharedView;
  selectedCaseId: string | null;
  rowHref: (caseId: string) => string;
}) {
  const definition = sharedViewDefinition(view);
  const apiView = definition.apiView ?? "all_active";
  const query = useCaseList({ view: apiView });
  const items = flattenPages(query.data);

  return (
    <AsyncPanel
      isLoading={query.isPending}
      error={query.error}
      isEmpty={items.length === 0}
      onRetry={() => {
        void query.refetch();
      }}
      loadingLabel={`Loading ${definition.label}…`}
      emptyTitle={`No cases in ${definition.label}`}
      emptyDescription={definition.emptyHint}
      className="shared-list__state"
    >
      <ul className="case-rows" aria-label={`${definition.label} cases`}>
        {items.map((item) => (
          <CaseRow
            key={item.id}
            item={item}
            selected={item.id === selectedCaseId}
            href={rowHref(item.id)}
          />
        ))}
      </ul>
      {query.hasNextPage ? (
        <div className="case-rows__more">
          <Button
            variant="secondary"
            busy={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </AsyncPanel>
  );
}

function SearchResults({
  query: rawQuery,
  selectedCaseId,
  rowHref,
}: {
  query: string;
  selectedCaseId: string | null;
  rowHref: (caseId: string) => string;
}) {
  const debounced = useDebouncedValue(rawQuery.trim(), SEARCH_DEBOUNCE_MS);
  const search = useCaseSearch(debounced);
  const hits = flattenPages(search.data);

  if (debounced.length === 0) {
    return (
      <div className="shared-list__state" data-state="idle">
        <div className="async-panel__message">
          <p className="shared-list__prompt">
            {sharedViewDefinition("search").emptyHint}
          </p>
        </div>
      </div>
    );
  }

  return (
    <AsyncPanel
      isLoading={search.isPending}
      error={search.error}
      isEmpty={hits.length === 0}
      onRetry={() => {
        void search.refetch();
      }}
      loadingLabel="Searching…"
      emptyTitle={`No cases match “${debounced}”`}
      emptyDescription="Check the spelling, or try the RO number, the last eight of the VIN or the customer's name."
      className="shared-list__state"
    >
      <ul className="case-rows" aria-label="Search results">
        {hits.map((hit) => (
          <CaseRow
            key={hit.case.id}
            item={hit.case}
            lifecycle={hit.lifecycle}
            selected={hit.case.id === selectedCaseId}
            href={rowHref(hit.case.id)}
          />
        ))}
      </ul>
      {search.hasNextPage ? (
        <div className="case-rows__more">
          <Button
            variant="secondary"
            busy={search.isFetchingNextPage}
            onClick={() => {
              void search.fetchNextPage();
            }}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </AsyncPanel>
  );
}

function CaseRow({
  item,
  lifecycle,
  selected,
  href,
}: {
  item: CaseListItem;
  lifecycle?: SearchHit["lifecycle"];
  selected: boolean;
  href: string;
}) {
  return (
    <li className="case-rows__item">
      <Link
        to={href}
        replace={selected}
        className={clsx("case-row", selected && "case-row--selected")}
        aria-current={selected ? "page" : undefined}
        data-case-id={item.id}
        data-unread={item.unread ? "true" : undefined}
        data-lifecycle={lifecycle}
      >
        <span className="case-row__top">
          <span className="case-row__vehicle">{item.vehicleLabel}</span>
          {item.unread ? (
            <span className="case-row__unread">
              <span className="visually-hidden">Unread</span>
            </span>
          ) : null}
        </span>
        <span className="case-row__identity">
          <span className="case-row__customer">{item.customerName}</span>
          <span className="case-row__dot" aria-hidden="true">
            ·
          </span>
          <span className="case-row__ro">
            {item.roNumber ? `RO ${item.roNumber}` : "Pre-RO"}
          </span>
          <span className="case-row__dot" aria-hidden="true">
            ·
          </span>
          <span className="case-row__number shared-case__mono">{item.caseNumber}</span>
        </span>
        <span className="case-row__status">
          <StatusBadge status={item.status} needsAttention={item.needsAttention} />
          <span className="case-row__progress">
            {item.quantityVerified} of {item.quantityOrdered} verified
          </span>
          {lifecycle ? (
            <span className="lifecycle-label" data-lifecycle={lifecycle}>
              {LIFECYCLE_LABELS[lifecycle]}
            </span>
          ) : null}
        </span>
        <span className="case-row__meta">
          <span>{item.advisorName}</span>
          <span className="case-row__dot" aria-hidden="true">
            ·
          </span>
          <time dateTime={item.updatedAt}>{formatRowTime(item.updatedAt)}</time>
        </span>
        <ChevronRight className="case-row__chevron" aria-hidden="true" focusable="false" />
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Detail pane
// ---------------------------------------------------------------------------

function DetailPane({ caseId, backHref }: { caseId: string; backHref: string }) {
  const query = useCaseDetail(caseId);
  return (
    <AsyncPanel
      isLoading={query.isPending}
      error={query.error}
      onRetry={() => {
        void query.refetch();
      }}
      loadingLabel="Loading case…"
      className="shared-detail-pane__state"
    >
      {query.data ? (
        <SharedCaseDetail
          detail={query.data}
          backHref={backHref}
          // A re-opened case renders from the cache while the open's own
          // fetch is in flight — or paused offline; the read acknowledgement
          // waits for its answer either way.
          isFetching={query.isFetching || query.isPaused}
        />
      ) : null}
    </AsyncPanel>
  );
}
